// components/WorkInProgress.jsx
import React, { useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useMobileDetection } from '../hooks/useMobileDetection';


const WorkInProgress = () => {
    const { isMobile, isTouch } = useMobileDetection();

    const cursorX = useMotionValue(0);
    const cursorY = useMotionValue(0);

    const springConfig = { damping: 25, stiffness: 150 };
    const cursorXSpring = useSpring(cursorX, springConfig);
    const cursorYSpring = useSpring(cursorY, springConfig);

    const slowSpringConfig = { damping: 30, stiffness: 100 };
    const cursorXSlow = useSpring(cursorX, slowSpringConfig);
    const cursorYSlow = useSpring(cursorY, slowSpringConfig);

    useEffect(() => {
        if (!isMobile && !isTouch) {
            const handleMouseMove = (e) => {
                const x = (e.clientX / window.innerWidth - 0.5) * 2;
                const y = (e.clientY / window.innerHeight - 0.5) * 2;
                cursorX.set(x * 30);
                cursorY.set(y * 30);
            };

            window.addEventListener('mousemove', handleMouseMove);
            return () => window.removeEventListener('mousemove', handleMouseMove);
        }
    }, [isMobile, isTouch, cursorX, cursorY]);

    return (
        <div className="fixed inset-0 bg-[#0a0a0a] overflow-hidden">
            {/* Organic gradient blobs */}
            <motion.div
                style={{
                    x: isMobile ? 1 : cursorXSpring,
                    y: isMobile ? 10 : cursorYSpring,
                    top: isMobile ? '5%' : '25%',
                    background: 'radial-gradient(circle, #ff6b00 0%, #ff8c42 50%, transparent 70%)',
                    filter: isMobile ? 'blur(60px)' : 'blur(100px)',
                }}
                className="absolute -left-1/4 w-[80vw] h-[80vw] sm:w-[600px] sm:h-[600px] rounded-full opacity-20"
                animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 90, 0],
                }}
                transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <motion.div
                style={{
                    x: isMobile ? 0 : cursorXSlow,
                    y: isMobile ? 0 : cursorYSlow,
                    bottom: isMobile ? '5%' : '25%',
                    background: 'radial-gradient(circle, #ff8c42 0%, #ffa066 50%, transparent 70%)',
                    filter: isMobile ? 'blur(70px)' : 'blur(120px)',
                }}
                className="absolute -right-1/4 w-[70vw] h-[70vw] sm:w-[500px] sm:h-[500px] rounded-full opacity-25"
                animate={{
                    scale: [1, 1.3, 1],
                    rotate: [0, -90, 0],
                }}
                transition={{
                    duration: 25,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 5
                }}
            />

            {/* Film grain texture */}
            <div
                className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulance type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                }}
            />

            {/* Main content - MOBILE FIRST LAYOUT */}
            <div className="relative h-full flex flex-col p-6 sm:p-8 md:p-12 lg:p-16">
                {/* Header - Fixed top */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex justify-between items-start gap-6 sm:gap-4 mb-12 sm:mb-0"
                >
                    <div className="flex-1 min-w-0 space-y-2 sm:space-y-1">
                        <h1
                            className="text-white/90 text-base sm:text-lg md:text-xl font-light tracking-wide"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            Paolo Allegretti
                        </h1>
                        <p
                            className="text-[#ff8c42]/60 text-xs sm:text-xs md:text-sm tracking-[0.15em] sm:tracking-wider uppercase"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            Broadcast Designer / Full-Stack Developer
                        </p>
                    </div>

                    <motion.div
                        animate={{
                            rotate: [0, 360],
                        }}
                        transition={{
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                        className="w-12 h-12 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full border border-[#ff8c42]/30 flex items-center justify-center flex-shrink-0"
                    >
                        <span className="text-[#ff8c42] text-xs sm:text-xs font-light">2025</span>
                    </motion.div>
                </motion.div>

                {/* Center massive title - MOBILE OPTIMIZED */}
                <motion.div
                    className="flex-1 flex items-center justify-center"
                    style={{
                        x: isMobile ? 0 : cursorXSpring,
                        y: isMobile ? 0 : cursorYSpring,
                    }}
                >
                    <div className="text-center w-full">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.4 }}
                            className="space-y-4 sm:space-y-6 md:space-y-10"
                        >
                            <motion.h2
                                className="text-white leading-[0.85] px-4 sm:px-0"
                                style={{
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: isMobile
                                        ? window.innerWidth < 375 ? '6rem'
                                            : window.innerWidth < 430 ? '7.5rem'
                                                : '9rem'
                                        : 'clamp(4rem, 12vw, 12rem)',
                                    fontWeight: '900',
                                    letterSpacing: '-0.05em',
                                }}
                            >
                                Rebuilding
                            </motion.h2>

                            <motion.div
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 1.2, delay: 0.8 }}
                                className="h-px w-16 sm:w-32 md:w-48 mx-auto"
                                style={{
                                    background: 'linear-gradient(90deg, transparent, #ff8c42, transparent)',
                                }}
                            />

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 1 }}
                                className="text-white/40 text-[10px] sm:text-sm md:text-base tracking-[0.25em] sm:tracking-[0.4em] uppercase font-light"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                                In Progress
                            </motion.p>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Footer - MOBILE COMPACT */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="space-y-6 sm:space-y-0 sm:flex sm:justify-between sm:items-end mt-8 sm:mt-0"
                >
                    <div className="space-y-3 sm:space-y-2">
                        <motion.a
                            href="mailto:info@paoloallegretti.com"
                            className="block text-white/60 hover:text-[#ff8c42] text-xs sm:text-base transition-colors duration-300 font-light tracking-wide"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            whileHover={{ x: isMobile ? 0 : 4 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            info@paoloallegretti.com
                        </motion.a>

                        <div className="flex items-center gap-3 text-white/30 text-[9px] sm:text-xs">
                            <span>Rome, IT</span>
                            <span className="w-0.5 h-0.5 rounded-full bg-white/30" />
                            <span>Available Soon</span>
                        </div>
                    </div>

                    <motion.div
                        className="flex gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                    >
                        {[...Array(3)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#ff8c42]"
                                animate={{
                                    opacity: [0.3, 1, 0.3],
                                    scale: [1, 1.2, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    delay: i * 0.3,
                                }}
                            />
                        ))}
                    </motion.div>
                </motion.div>
            </div>

        </div>
    );
};

export default WorkInProgress;